import React, { useState, useEffect } from 'react';
import APIService from '../services/api';
import CvEditeurLauncher from './CvEditeurLauncher';
import { avecCle } from '../services/cleLecture';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

// Boutons réutilisés
const gBtn = { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 11px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, textDecoration: 'none', cursor: 'pointer' };

/**
 * Viewer de document canonique — pointe TOUJOURS sur un endpoint back (jamais
 * sur une URL de stockage externe kDrive, sinon « Failed to fetch » CORS) :
 *   GET /candidates/:candidateId/documents/:docId/preview   (candidate_documents)
 *   GET /position-documents/:id/preview                     (docs de positionnement)
 *     ?download=1  → force le téléchargement
 *     ?cle=<clé de lecture> → ouvrable dans un <iframe> (pas de header possible)
 * PDF/images servis bruts ; docx/pptx/xlsx convertis en PDF automatiquement.
 * À réutiliser partout (Candidats, AO, Pipeline, Vivier) — ne plus recoder de viewer par écran.
 *
 * `previewBase` : base d'URL back explicite (sans query) — passer pour un document
 * de positionnement (`…/position-documents/:id/preview`). Sinon on construit
 * l'URL candidate_documents depuis candidateId + docId.
 */
// zIndex : l'aperçu doit pouvoir passer au-dessus d'une modale qui l'a ouvert
// (le sélecteur de CV du dossier AO est à 600).
// `cv` : le document est un CV — déposé ou produit par le générateur. C'est
// la condition d'affichage du bouton « Éditer », et elle se calcule avec
// `estCvDocument` (src/utils/cvDocument.js), jamais à la main : chaque écran
// avait sa version de la règle, et deux d'entre eux répondaient « non »
// toujours. Un CV sans préparation s'édite : le lanceur en crée une par
// reprise fidèle du fichier. Ce qui reste exclu, c'est ce qui n'est pas un CV
// (matrice de compétences, CNI, contrat) — l'éditeur n'y ferait rien.
// CE QU'UN CADRE SAIT AFFICHER. Mesuré au navigateur le 10.09 : un contenu
// typé « application/octet-stream » (ou un type bureautique brut) placé dans
// un <iframe> ne s'affiche PAS — le navigateur y voit un téléchargement, un
// téléchargement lancé depuis un cadre est refusé, et il n'en reste RIEN :
// cadre blanc, aucune erreur, aucun événement. C'est le « (blocked:other) »
// du journal réseau, et c'est ce que voyait l'utilisateur.
//
// Le serveur refuse désormais ces aperçus en le disant ; cette garde tient
// pour un serveur pas encore à jour — et parce qu'un aperçu muet est le pire
// des états : on ne sait pas s'il charge, s'il est vide ou s'il est cassé.
const affichableInline = (t) => /^(application\/pdf|image\/(?!svg)|text\/plain)/i.test(t || '');

export default function DocumentViewer({ candidateId, docId, label, previewBase, onClose, zIndex = 500, cv, besoinId, candidatNom, showNotification }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const [inaffichable, setInaffichable] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [editer, setEditer] = useState(false);
  // Incrémenté à la fermeture de l'éditeur : l'édition régénère le document
  // côté serveur, l'aperçu doit montrer le fichier d'APRÈS, jamais celui
  // chargé avant les modifications.
  const [version, setVersion] = useState(0);
  const editable = candidateId != null && cv === true;
  const token = APIService.getToken();
  const base = previewBase || `${BASE}/candidates/${candidateId}/documents/${docId}/preview`;
  const sep = base.includes('?') ? '&' : '?';
  // Clé de lecture (30 min, lecture seule) — jamais le jeton de session (28.09).
  const dl = avecCle(`${base}${sep}download=1`);
  const name = (label || '').toLowerCase();
  const isImage = /\.(jpe?g|png|gif|webp|bmp|svg)$/.test(name);

  // Le fichier est récupéré AVANT affichage : un refus du serveur (« Document
  // non trouvé »…) devient un message clair — jamais du JSON brut dans le
  // cadre, ce que faisait l'iframe pointée directement sur l'URL.
  useEffect(() => {
    let annule = false, cree = null;
    setLoading(true); setError(false); setErrMsg(null); setInaffichable(null); setBlobUrl(null);
    (async () => {
      try {
        const r = await fetch(base, { headers: { Authorization: `Bearer ${token}` } });
        const ct = r.headers.get('content-type') || '';
        if (!r.ok || ct.includes('application/json')) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.error || j.message || `HTTP ${r.status}`);
        }
        const b = await r.blob();
        if (annule) return;
        // Un type que le cadre ne sait pas rendre ne part PAS dans l'iframe :
        // il n'y produirait rien du tout. On le dit, et on propose ce qui
        // marche — le téléchargement.
        if (!affichableInline(b.type)) { setInaffichable(b.type || null); setLoading(false); return; }
        cree = URL.createObjectURL(b);
        setBlobUrl(cree); setLoading(false);
      } catch (e) {
        if (!annule) { setError(true); setErrMsg(e.message); setLoading(false); }
      }
    })();
    return () => { annule = true; if (cree) URL.revokeObjectURL(cree); };
  }, [base, token, version]);

  // Le document n'existe plus côté serveur (supprimé de la fiche…) : le
  // télécharger n'a pas de sens, et la cause mérite d'être dite.
  const introuvable = error && /non trouv|introuvable/i.test(errMsg || '');

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex, padding: '24px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel, #fff)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '960px', height: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderBottom: '1px solid var(--hair)', flexShrink: 0 }}>
          <BI icon="file-earmark-text" style={{ color: 'var(--text-3)', flex: 'none' }} />
          <span style={{ flex: 1, minWidth: 0, fontSize: '14px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label || 'Document'}</span>
          {editable && (
            <button onClick={() => setEditer(true)} style={{ ...gBtn }} title="Modifier le contenu du CV (sections, expériences, compétences)">
              <BI icon="pencil-square" /> Éditer
            </button>
          )}
          <a href={dl} style={{ ...gBtn }} title="Télécharger"><BI icon="download" /></a>
          <button onClick={onClose} style={{ ...gBtn }} title="Fermer"><BI icon="x-lg" /></button>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: 'var(--bg)', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {loading && !error && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', pointerEvents: 'none' }}>
              <BI icon="arrow-repeat" style={{ fontSize: '22px', animation: 'dv-spin .7s linear infinite' }} />
            </div>
          )}
          <style>{`@keyframes dv-spin{to{transform:rotate(360deg)}}`}</style>
          {inaffichable !== null && !error ? (
            <div style={{ textAlign: 'center', color: 'var(--text-2)', padding: '24px', maxWidth: '440px' }}>
              <BI icon="file-earmark-arrow-down" style={{ fontSize: '24px', color: 'var(--text-3)' }} />
              <div style={{ marginTop: '8px', fontSize: '13.5px', fontWeight: 600 }}>
                Ce document ne s'affiche pas dans l'aperçu
              </div>
              <p style={{ marginTop: '5px', fontSize: '12px', lineHeight: 1.6, color: 'var(--text-3)' }}>
                Son format n'est pas lisible directement dans le navigateur. Téléchargez-le pour l'ouvrir
                avec l'application de votre poste.
              </p>
              <a href={dl} style={{ ...gBtn, marginTop: '14px' }}><BI icon="download" /> Télécharger</a>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', color: 'var(--text-2)', padding: '24px', maxWidth: '440px' }}>
              <BI icon="exclamation-triangle" style={{ fontSize: '24px', color: '#d97706' }} />
              <div style={{ marginTop: '8px', fontSize: '13.5px', fontWeight: 600 }}>
                {introuvable ? 'Ce document n\'existe plus' : 'Impossible d\'afficher le document'}
              </div>
              {errMsg && <div style={{ marginTop: '5px', fontSize: '12px', color: 'var(--text-3)' }}>{errMsg}</div>}
              {introuvable ? (
                <p style={{ marginTop: '10px', fontSize: '12px', lineHeight: 1.6, color: 'var(--text-3)' }}>
                  Le fichier a probablement été supprimé de la fiche du candidat. Si une pièce de dossier
                  pointe encore dessus, relancez « Rescanner » sur le dossier pour remettre ses boutons à jour.
                </p>
              ) : (
                <a href={dl} style={{ ...gBtn, marginTop: '14px' }}><BI icon="download" /> Télécharger</a>
              )}
            </div>
          ) : blobUrl && isImage ? (
            <img src={blobUrl} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : blobUrl ? (
            // PAS de `sandbox` sur ce cadre — et ce n'est pas un oubli.
            // Mesuré le 09.09 sur Chromium : le lecteur PDF du navigateur ne
            // s'affiche dans AUCUN cadre bac-à-sable — ni avec allow-scripts,
            // ni même en y ajoutant allow-same-origin. Le cadre rend une page
            // vide. L'attribut ne durcissait donc pas l'aperçu, il le
            // supprimait (vécu : « le visualiseur de matrice ne fonctionne
            // plus », et avant lui les CV). Le durcissement est côté serveur,
            // à la source : un format qu'un navigateur EXÉCUTE (SVG, HTML) est
            // servi en téléchargement, jamais en ligne — utils/documentServer.
            <iframe src={blobUrl} title={label || 'Document'}
              style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} />
          ) : null}
        </div>
      </div>

      {/* L'éditeur passe au-dessus de l'aperçu : on compare le rendu et on corrige. */}
      {editer && (
        <div onClick={e => e.stopPropagation()}>
          <CvEditeurLauncher candidateId={candidateId} besoinId={besoinId} docId={docId}
            candidatNom={candidatNom} showNotification={showNotification}
            zIndex={zIndex + 200} onClose={() => { setEditer(false); setVersion(v => v + 1); }} />
        </div>
      )}
    </div>
  );
}
